import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Divider, useTheme } from '@mui/material'
import { Box, Card, Collapse, IconButton, Stack, Typography } from '@mui/material'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import PropertyFlood from '../components/climateAnalytics/property/PropertyFlood'
import Page from '../components/Page'
import { useClimateHazards } from '../hooks/analytics'
import { useParams } from 'react-router-dom'
import LoadingIcon from '../components/LoadingIcon'
import { EmissionTier } from '../components/climateAnalytics/common'
import PropertyWildfire from '../components/climateAnalytics/property/PropertyWildfire'
import PropertyHeatStress from '../components/climateAnalytics/property/PropertyHeatStress'
import PropertyExtremeWind from '../components/climateAnalytics/property/PropertyExtremeWind'
import PropertyWaterRisk from '../components/climateAnalytics/property/PropertyWaterRisk'
import siteMap from '../siteMap'
import EmissionTierSelector from '../components/climateAnalytics/common/EmissionTierSelector'
import HazardSelector from '../components/climateAnalytics/common/HazardSelector'
import ClimateContextBar from '../components/climateAnalytics/common/ClimateContextBar'
import { useLocation as useProperty } from '../hooks/locations'
import { useCurrentUser } from '../hooks/users'
import Map, { MapRef, Marker } from 'react-map-gl'
import ConfigProvider from 'balkerne-core/config'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useDispatch } from 'react-redux'
import systemActions from '../store/system'

const PropertyClimateAnalytics = () => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const hazardMatch = useRouteMatch<{ hazard?: string }>(`${siteMap.PropertyClimateAnalytics.path}/:hazard`)
  const { id: propertyId } = useParams()
  const { data: property, isLoading: isPropertyLoading } = useProperty(propertyId)
  const { data: hazards, isLoading: isHazardsLoading } = useClimateHazards()
  const { currencyISO } = useCurrentUser()
  const [selectedHazardCode, setSelectedHazardCode] = useState<string | null>(null)
  const mapRef = useRef<MapRef | null>(null)
  const [emissionTier, setEmissionTier] = useState<EmissionTier>(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return EmissionTier.HIGH
  })
  const [showPropertyDetails, setShowPropertyDetails] = useState(false)

  useEffect(() => {
    dispatch(systemActions.setPropertyId(Number.parseInt(propertyId)))
  }, [propertyId])

  const propertyAddress = useMemo(() => {
    const parts = [
      property?.address?.street,
      property?.address?.town,
      property?.address?.postcode,
      property?.address?.country,
    ].filter(Boolean)
    return parts.join(', ')
  }, [property])

  const formattedInsurance = useMemo(() => {
    if (!property?.insurance || property.insurance.structure == null) return null
    const currency = currencyISO || 'GBP'
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(property.insurance.structure)
  }, [currencyISO, property])

  const mapViewState = useMemo(
    () => ({
      longitude: property?.coordinates?.longitude ?? -0.1276,
      latitude: property?.coordinates?.latitude ?? 51.5072,
      zoom: property?.coordinates ? 12 : 2,
    }),
    [property],
  )

  const resetMapView = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    map.stop()
    map.jumpTo({
      center: [mapViewState.longitude, mapViewState.latitude],
      zoom: mapViewState.zoom,
      bearing: 0,
      pitch: 0,
    })
  }, [mapViewState])

  useEffect(() => {
    resetMapView()
  }, [resetMapView])

  // property: object (example)
  // {
  //     "id": 9635,
  //     "organisation_id": 1,
  //     "name": "82 Manchester Road",
  //     "type": {
  //         "id": "196c16a3-cc9d-5f28-90f2-c0272fe6a352",
  //         "is_system": true,
  //         "label": "Unknown",
  //         "created_at": "2025-11-30T21:39:24.920771"
  //     },
  //     "property_customer_reference": "3245b097-c506-4d60-8615-6264f1c14e2b",
  //     "coordinates": {
  //         "latitude": 53.58436225297437,
  //         "longitude": -2.210266880526774
  //     },
  //     "created_at": "2025-11-30T21:39:25.206944",
  //     "geometry": null,
  //     "address": {
  //         "country": "England",
  //         "town": "Heywood",
  //         "postcode": "OL10 2PN",
  //         "street": "Manchester Road"
  //     },
  //     "attributes": [],
  //     "groups": [],
  //     "insurance": {
  //         "structure": 3000000,
  //         "contents": null,
  //         "revenue": null
  //     },
  //     "metadata": {
  //         "distance": null
  //     }
  // }
  const hazardFromRoute = hazardMatch?.params?.hazard
  const emissionFromQuery = useMemo(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return null
  }, [location.search])

  const lastSearchRef = useRef(location.search)

  const selectHazard = useMemo(() => {
    return hazards?.find(h => h.name === selectedHazardCode)
  }, [hazards, selectedHazardCode])

  useEffect(() => {
    if (!hazards.length) return
    const fallbackHazard = hazards[0]?.name ?? null
    const nextHazard = hazards.find(h => h.name === hazardFromRoute)?.name ?? fallbackHazard
    if (!nextHazard) return

    if (selectedHazardCode !== nextHazard) {
      setSelectedHazardCode(nextHazard)
    }

    const targetPath = siteMap.PropertyClimateAnalytics.getPath(propertyId, nextHazard, emissionTier)
    const currentPath = `${location.pathname}${location.search}`
    if (currentPath !== targetPath) {
      history.replace(targetPath)
    }
  }, [
    emissionTier,
    hazards,
    hazardFromRoute,
    history,
    location.pathname,
    location.search,
    propertyId,
    selectedHazardCode,
  ])

  useEffect(() => {
    if (lastSearchRef.current === location.search) return
    lastSearchRef.current = location.search
    if (emissionFromQuery && emissionFromQuery !== emissionTier) {
      setEmissionTier(emissionFromQuery)
    }
  }, [emissionFromQuery, emissionTier, location.search])

  const renderHazardComponent = useMemo(() => {
    switch (selectedHazardCode) {
      case 'fld':
        return <PropertyFlood emissionTier={emissionTier as string} propertyId={propertyId} />
      case 'wf':
        return <PropertyWildfire emissionTier={emissionTier as string} propertyId={propertyId} />
      case 'hs':
        return <PropertyHeatStress emissionTier={emissionTier as string} propertyId={propertyId} />
      case 'tc':
        return <PropertyExtremeWind emissionTier={emissionTier} propertyId={propertyId} />
      case 'wr':
        return <PropertyWaterRisk emissionTier={emissionTier as string} propertyId={propertyId} />
      default:
        return null
    }
  }, [selectedHazardCode, emissionTier, propertyId])

  if (isHazardsLoading) {
    return (
      <Page size="lg" title="Climate Analytics" back>
        <Stack
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          sx={{
            height: '200px',
            width: '100%',
          }}>
          <LoadingIcon />
        </Stack>
      </Page>
    )
  }

  return (
    <Page size="lg" title="Climate Analytics" back>
      <Stack direction="column" spacing={3}>
        {selectedHazardCode == null ? (
          <Stack
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            sx={{
              height: '100px',
              width: '100%',
            }}>
            <Typography variant="h6">Select a hazard to view analytics</Typography>
          </Stack>
        ) : (
          <>
            <Stack spacing={1.5}>
              <Box sx={{ minWidth: 0, maxWidth: 760 }}>
                <Typography variant="h4">{selectHazard?.display_name ?? 'Hazard'}</Typography>
                {selectHazard?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {selectHazard.description}
                  </Typography>
                )}
              </Box>

              <ClimateContextBar
                hazardControl={
                  <HazardSelector
                    hazards={hazards}
                    value={selectedHazardCode ?? ''}
                    onChange={newHazard => {
                      setSelectedHazardCode(newHazard)
                      history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, newHazard, emissionTier))
                    }}
                  />
                }
                emissionControl={<EmissionTierSelector value={emissionTier} onChange={setEmissionTier} />}
              />
            </Stack>

            <Box
              sx={{
                height: '100%',
                width: '100%',
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
              }}>
              <Card variant="outlined">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="overline">Property</Typography>
                    <Typography variant="h6">{property?.name ?? 'Property'}</Typography>
                  </Stack>
                  <IconButton
                    aria-label="Toggle property details"
                    onClick={() => setShowPropertyDetails(prev => !prev)}
                    size="small">
                    {showPropertyDetails ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Stack>
                <Collapse in={showPropertyDetails} timeout="auto" unmountOnExit>
                  <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Type: {property?.type?.label ?? 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Address: {propertyAddress || 'Unavailable'}
                    </Typography>
                    {formattedInsurance && (
                      <Typography variant="body2" color="text.secondary">
                        Insured value: {formattedInsurance}
                      </Typography>
                    )}
                    {property?.coordinates ? (
                      <Box sx={{ height: 200, borderRadius: 1, overflow: 'hidden' }}>
                        <Map
                          ref={mapRef}
                          reuseMaps
                          initialViewState={mapViewState}
                          mapStyle="mapbox://styles/mapbox/streets-v11"
                          style={{ width: '100%', height: '100%' }}
                          mapboxAccessToken={ConfigProvider.mapboxPublicKey}
                          onLoad={resetMapView}
                          scrollZoom={false}
                          doubleClickZoom={false}
                          dragPan={false}
                          dragRotate={false}
                          keyboard={false}>
                          <Marker
                            longitude={property.coordinates.longitude}
                            latitude={property.coordinates.latitude}
                            anchor="bottom">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                border: `2px solid ${theme.palette.background.paper}`,
                              }}
                            />
                          </Marker>
                        </Map>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.default,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Typography variant="body2" color="text.secondary">
                          {isPropertyLoading ? 'Loading location...' : 'No coordinates available'}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Collapse>
              </Card>
            </Box>

            <Divider />

            <Box
              sx={{
                height: '100%',
                width: '100%',
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
              }}>
              {renderHazardComponent}
            </Box>
          </>
        )}
      </Stack>
    </Page>
  )
}

export default PropertyClimateAnalytics
